const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([^<>()[\]\\.,;:\s@"]+\.)+[^<>()[\]\\.,;:\s@"]{2,})$/i;

const isEmail = (email: string) => {
  return emailRegex.test(email);
}

const isValidHex = (hex: string) => {
  const hexRegex = /^#([0-9A-F]{3}){1,2}$/i;
  return hexRegex.test(hex);
};

/**
 * Converts a hex color string to an RGB object
 * Compatible with hex-rgb library API
 * @param hex - Hex color string (e.g., "#FF0000", "FF0000", "#F00", "#FF0000CC", "#F00A")
 * @param options - Optional configuration object
 * @param options.format - Output format: 'object' (default), 'array', or 'css'
 * @param options.alpha - Override alpha value (0-1), overriding any alpha in hex color
 * @returns Object with red, green, blue, and alpha properties (or array/css string based on format)
 */
const hexToRgb = (hex: string, options?: { format?: 'object' | 'array' | 'css'; alpha?: number }) => {
  // Remove # if present
  let cleanHex = hex.replace(/^#/, '');
  
  let alpha = 1; // Default alpha value
  
  // Handle 3-digit hex codes (e.g., #F00 -> FF0000)
  if (cleanHex.length === 3) {
    cleanHex = cleanHex.split('').map(char => char + char).join('');
  }
  // Handle 4-digit hex codes with alpha (e.g., #F00A -> FF0000AA)
  else if (cleanHex.length === 4) {
    const rgbPart = cleanHex.substring(0, 3).split('').map(char => char + char).join('');
    const alphaPart = cleanHex.substring(3, 4) + cleanHex.substring(3, 4);
    cleanHex = rgbPart;
    alpha = parseInt(alphaPart, 16) / 255;
  }
  // Handle 6-digit hex codes (e.g., #FF0000)
  else if (cleanHex.length === 6) {
    // No alpha in hex, keep default alpha = 1
  }
  // Handle 8-digit hex codes with alpha (e.g., #FF0000CC)
  else if (cleanHex.length === 8) {
    alpha = parseInt(cleanHex.substring(6, 8), 16) / 255;
    cleanHex = cleanHex.substring(0, 6);
  }
  else {
    throw new Error(`Invalid hex color: ${hex}`);
  }
  
  // Validate hex length (should be 6 after processing)
  if (cleanHex.length !== 6) {
    throw new Error(`Invalid hex color: ${hex}`);
  }
  
  // Parse RGB values
  const red = parseInt(cleanHex.substring(0, 2), 16);
  const green = parseInt(cleanHex.substring(2, 4), 16);
  const blue = parseInt(cleanHex.substring(4, 6), 16);
  
  // Override alpha if provided in options
  if (typeof options?.alpha === 'number') {
    alpha = options.alpha;
  }
  
  const format = options?.format || 'object';
  
  // Return the result in the specified format
  switch (format) {
    case 'array':
      return [red, green, blue, alpha];
    case 'css':
      // Return modern CSS color format: rgb(r g b / alpha%)
      if (alpha < 1) {
        return `rgb(${red} ${green} ${blue} / ${Math.round(alpha * 100)}%)`;
      } else {
        return `rgb(${red} ${green} ${blue})`;
      }
    case 'object':
    default:
      return { red, green, blue, alpha };
  }
};

const searchTitle = (title: string, searchValue: string) => {
  const escapedSearchValue = searchValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const parts = title.split(new RegExp(`(${escapedSearchValue})`, 'gi'));

  let hasMatch = false;
  const result = parts.reduce((acc: any[], part: string) => {
    const isMatched = part.toLowerCase() === searchValue.toLowerCase();
    acc.push({ value: part, isMatched } as any);
    if (isMatched) {
      hasMatch = true;
    }
    return acc;
  }, []);

  if (!hasMatch) return false;
  return result;
}

const stringUtils = {
  isEmail,
  isValidHex,
  searchTitle,
  hexToRgb,
}

export default stringUtils;